import type { FunctionComponent } from 'react';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import _ from 'underscore';

import { Analytics } from '@trello/atlassian-analytics';
import { getNonPublicIfAvailable } from '@trello/business-logic/member';
import { sendErrorEvent } from '@trello/error-reporting';
import { Button } from '@trello/nachos/button';
import { AddMemberIcon } from '@trello/nachos/icons/add-member';
import { Popover, usePopover } from '@trello/nachos/popover';
import { Spinner } from '@trello/nachos/spinner';
import { dangerouslyConvertPrivacyString } from '@trello/privacy';
import type { AppManagementTestIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';

import { useAddPluginCollaboratorMutation } from '../queries/AddPluginCollaboratorMutation.generated';
import type { PluginCollaboratorsQuery } from '../queries/PluginCollaboratorsQuery.generated';
import type { PluginOrganizationMembersQuery } from '../queries/PluginOrganizationMembersQuery.generated';

import styles from './AddCollaboratorsButton.less';

type Plugin = NonNullable<PluginCollaboratorsQuery['plugin']>;
type Organization = NonNullable<PluginOrganizationMembersQuery['organization']>;
type Memberships = Organization['memberships'];
type Members = NonNullable<Plugin['collaborators']>;
type Member = Memberships[number]['member'];

export const AddCollaboratorItem: FunctionComponent<{
  idPlugin: string;
  idOrganizationOwner: string;
  member: Member;
  refetch: () => void;
  refetching: boolean;
  setAdding: (id: string) => void;
}> = ({
  idPlugin,
  idOrganizationOwner,
  member,
  refetch,
  refetching,
  setAdding,
}) => {
  const [addPluginCollaboratorMutation, { loading }] =
    useAddPluginCollaboratorMutation();
  const fullName = getNonPublicIfAvailable(member, 'fullName');
  const avatarUrl = getNonPublicIfAvailable(member, 'avatarUrl');
  const initials = getNonPublicIfAvailable(member, 'initials');
  const { id, username } = member;

  if (loading || refetching) {
    return (
      <div className={styles.memberItemSpinner}>
        <Spinner
          testId={getTestId<AppManagementTestIds>(
            'power-up-collaborators-page-spinner',
          )}
        />
      </div>
    );
  }

  return (
    <div className={styles.memberItem}>
      <div className={styles.memberAvatar}>
        {avatarUrl ? (
          <img
            className={styles.memberImage}
            alt={''}
            src={[avatarUrl, '30.png'].join('/')}
            srcSet={
              [avatarUrl, '30.png 1x'].join('/') +
              ', ' +
              [avatarUrl, '50.png 2x'].join('/')
            }
          />
        ) : (
          <span className={styles.memberInitials}>{initials}</span>
        )}
      </div>
      <div className={styles.memberDetails}>
        <div className={styles.memberFullName}>{fullName}</div>
        <div
          className={styles.memberUsername}
        >{`@${dangerouslyConvertPrivacyString(username)}`}</div>
      </div>
      <div className={styles.memberOption}>
        <Button
          appearance="primary"
          className={styles.addCollaboratorButton}
          onClick={async () => {
            const traceId = Analytics.startTask({
              taskName: 'edit-plugin/addCollaborator',
              source: 'powerUpAdminCollaboratorsScreen',
              attributes: {
                idPlugin,
              },
            });
            try {
              Analytics.sendClickedButtonEvent({
                buttonName: 'powerUpAdminCollaboratorsAddButton',
                source: 'powerUpAdminCollaboratorsScreen',
                containers: {
                  organization: { id: idOrganizationOwner },
                },
              });
              setAdding(id);
              await addPluginCollaboratorMutation({
                variables: {
                  idPlugin,
                  idMember: id,
                },
              });
              Analytics.taskSucceeded({
                taskName: 'edit-plugin/addCollaborator',
                traceId,
                source: 'powerUpAdminCollaboratorsScreen',
                attributes: {
                  idPlugin,
                },
              });
              refetch();
            } catch (err) {
              Analytics.taskFailed({
                taskName: 'edit-plugin/addCollaborator',
                traceId,
                source: 'powerUpAdminCollaboratorsScreen',
                attributes: {
                  idPlugin,
                },
                error: err,
              });
              sendErrorEvent(err, {
                tags: {
                  ownershipArea: 'trello-web-eng',
                },
                extraData: {
                  component: 'PowerUpCollaboratorsAddCollaboratorsButton',
                  // @ts-expect-error
                  errorCode: err?.networkError?.extensions?.code ?? 'UNKNOWN',
                },
              });
            }
          }}
        >
          <FormattedMessage
            id="templates.app_management.add"
            defaultMessage="Add"
            description="Button text for adding a collaborator"
          />
        </Button>
      </div>
    </div>
  );
};

export const AddCollaboratorsButton: FunctionComponent<{
  idPlugin: string;
  memberships?: Memberships;
  collaborators?: Members;
  idOrganizationOwner: string;
  refetch: () => void;
  refetching: boolean;
}> = ({
  idPlugin,
  memberships,
  collaborators,
  idOrganizationOwner,
  refetch,
  refetching,
}) => {
  const intl = useIntl();
  const { triggerRef, toggle, popoverProps } = usePopover<HTMLButtonElement>();
  // We need to keep track of members who are in the process of being added
  // so we can show a spinner for those members.
  // The loading value from the mutation is not enough b/c it doesn't capture the refetching time.
  const [membersBeingAdded, setMembersBeingAdded] = useState<string[]>([]);
  const setAdding = (id: string) => {
    setMembersBeingAdded(membersBeingAdded.concat(id));
  };

  const [searchString, setSearchString] = useState('');
  const searchBar = (
    <input
      type="search"
      placeholder={intl.formatMessage({
        id: 'templates.app_management.search-members',
        defaultMessage: 'Search by name or username',
        description: 'Placeholder text for member search input',
      })}
      value={searchString}
      onChange={(e) => setSearchString(e.target.value)}
      className={styles.searchBar}
      autoFocus
    />
  );

  let content;

  useEffect(() => {
    // members that are done being added will be in the list of collaborators
    // so discard any members that are but keep the rest
    if (membersBeingAdded.length && collaborators?.length) {
      const filtered = membersBeingAdded.filter((id: string) => {
        return !_.some(collaborators, (c) => c.id === id);
      });
      if (!_.isEqual(filtered, membersBeingAdded)) {
        setMembersBeingAdded(filtered);
      }
    }
  }, [membersBeingAdded, collaborators]);

  if (!memberships || !collaborators) {
    content = <Spinner />;
  } else {
    content = memberships.map((membership) => {
      const member = membership.member;
      const { id } = member;

      // remove all members who are admins, deactivated, or already collaborators
      const isCollaborator = _.some(
        collaborators,
        (collaborator) => id === collaborator.id,
      );
      if (isCollaborator) {
        return null;
      }
      if (membership.deactivated) {
        return null;
      }
      if (membership.memberType === 'admin') {
        return null;
      }
      // Filter out by search string. Match name or username
      if (searchString.trim()) {
        const searchTerms = searchString.trim().toLowerCase().split(' ');
        const { fullName, username } = member;
        const names = [...(fullName?.split(' ') || []), username];
        const match = _.some(searchTerms, (term) =>
          _.some(names, (name) => name.trim().toLowerCase().startsWith(term)),
        );
        if (!match) {
          return null;
        }
      }
      const isRefetching = membersBeingAdded.includes(member.id) && refetching;
      return (
        <AddCollaboratorItem
          key={id}
          idPlugin={idPlugin}
          idOrganizationOwner={idOrganizationOwner}
          member={member}
          refetch={refetch}
          refetching={isRefetching}
          setAdding={setAdding}
        />
      );
    });
    if (!_.compact(content).length) {
      content = (
        <p>
          <FormattedMessage
            id="templates.app_management.no-members"
            defaultMessage="No members available."
            description="Message shown when no members are available to add"
          />
        </p>
      );
    }
  }

  return (
    <div className={styles.addCollaboratorsButtonSection}>
      <Button
        appearance="primary"
        className={styles.addCollaboratorsButton}
        ref={triggerRef}
        onClick={toggle}
        iconBefore={<AddMemberIcon size="small" />}
      >
        <FormattedMessage
          id="templates.app_management.add-collaborators"
          defaultMessage="Add collaborators"
          description="Button text for adding collaborators"
        />
      </Button>
      <Popover
        title={
          <FormattedMessage
            id="templates.app_management.add-collaborators"
            defaultMessage="Add collaborators"
            description="Title for the add collaborators popover"
          />
        }
        {...popoverProps}
        dangerous_disableAutoFocus
      >
        <div>{searchBar}</div>
        <div className={styles.addCollaboratorsPopoverContent}>{content}</div>
      </Popover>
    </div>
  );
};
